import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { normalizeFileName } from '@/shared/lib/normalize-file-name'
import { uploadFileToSignedOssUrl } from '@/shared/lib/uploadFileToSignedOssUrl'
import {
  assertUploadFileReadable,
  assertUploadFileSize,
} from '@/shared/lib/uploadGuardrails'
import type { MachineryStandardClass } from '@artem-lex/common-server/standards/iso12100'
import type {
  InformationForUseStructured,
  InformationForUseUpdatePayload,
  ProjectRelationItem,
  RiskReductionMeasuresPayload,
  RiskReductionMeasuresUpdatePayload,
  RiskReductionMeasuresResponse,
  SignificantHazardsPayload,
  SignificantHazardsResponse,
} from './standards'
import {
  applyWorkflowPollBatchEvents,
  getWorkflowStatus,
  WorkflowEventType,
} from './workflows'
import { RISK_REDUCTION_MEASURES_KEY } from './machineryDirectiveCompliance'

export type { ProjectRelationItem }

export const MANUAL_MACHINERY_STANDARD_TYPES = ['B', 'C'] as const satisfies readonly MachineryStandardClass[]

export type ManualMachineryStandardType =
  (typeof MANUAL_MACHINERY_STANDARD_TYPES)[number]

export function isManualMachineryStandardType(
  value: string | null | undefined
): value is ManualMachineryStandardType {
  if (value == null) {
    return false
  }
  return (MANUAL_MACHINERY_STANDARD_TYPES as readonly string[]).includes(value)
}

/** Predefined reason codes — must match server's ExtractionImpedimentReasonCode. */
export type ExtractionImpedimentReasonCode =
  | 'pdf_empty'
  | 'pdf_corrupted'
  | 'scope_not_found'
  | 'scope_extraction_failed'
  | 'ifu_not_found'
  | 'ifu_extraction_failed'
  | 'hazards_not_found'
  | 'hazards_extraction_failed'
  | 'rrms_not_found'
  | 'rrms_extraction_failed'
  | 'schema_validation_failed'

/** Reason why significant hazards extraction yielded no results (e.g. no table in document). */
export type SignificantHazardsImpediment = {
  noHazardsFound?: boolean
  reason?: ExtractionImpedimentReasonCode
}

/** Generic impediment for an extraction section (scope, information-for-use, etc.). */
export type ExtractionImpediment = {
  reason: ExtractionImpedimentReasonCode
}

/** Sections that can be extracted from a user standard PDF (must match server enum). */
export type StandardExtractSection =
  | 'scope'
  | 'significantHazards'
  | 'riskReductionMeasures'
  | 'informationForUse'

export type ExtractPreviewRequest = {
  extractSections?: StandardExtractSection[]
}

export type SectionLocalizeStreamSection = Exclude<
  StandardExtractSection,
  'scope'
>

export type SectionLocalizeStreamResult = {
  section: SectionLocalizeStreamSection
  significantHazards?: SignificantHazardsResponse
  informationForUse?: InformationForUseStructured
  riskReductionMeasures?: RiskReductionMeasuresResponse
}

export type SectionLocalizeStreamRequest = {
  section: SectionLocalizeStreamSection
  translationLocales: string[]
  preview: ExtractPreviewResponse
  onProgress?: (message: string) => void
}

function getUserStandardsStreamUrl(
  userStandardId: string,
  pathSuffix: string
): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}/v1/user-standards/${userStandardId}${pathSuffix}`
}

function getUserStandardsStreamHeaders(): Record<string, string> {
  const common = (apiClient.defaults.headers.common ?? {}) as Record<
    string,
    string
  >
  const { 'Content-Type': _ct, ...rest } = common
  return rest
}

function buildSectionLocalizeStreamBody(
  request: SectionLocalizeStreamRequest
): Record<string, unknown> {
  const { section, translationLocales, preview } = request
  const body: Record<string, unknown> = { section, translationLocales }
  if (section === 'significantHazards') {
    body.significantHazards = preview.significantHazards
  } else if (section === 'riskReductionMeasures') {
    body.riskReductionMeasures = {
      measures: preview.riskReductionMeasures?.measures ?? [],
    }
  } else if (section === 'informationForUse') {
    body.informationForUse = preview.informationForUse ?? { clauses: [] }
  }
  return body
}

/** Response from POST /v1/user-standards/:id/extract-preview */
export type ExtractPreviewResponse = {
  pdfImpediment: ExtractionImpediment | null
  significantHazards: SignificantHazardsResponse
  /** Set when extraction ran but no hazards table was found or extraction was not possible. */
  significantHazardsImpediment: SignificantHazardsImpediment | null
  informationForUse: InformationForUseStructured
  /** Set when extraction ran but no information-for-use requirements were found. */
  informationForUseImpediment: ExtractionImpediment | null
  scope: string | null
  /** Set when extraction ran but no scope section was found. */
  scopeImpediment: ExtractionImpediment | null
  riskReductionMeasures: RiskReductionMeasuresResponse
  /** Set when extraction ran but no RRMs were found or extraction was not possible. */
  riskReductionMeasuresImpediment: ExtractionImpediment | null
}

const USER_STANDARDS_BUCKET_PREFIX = 'user-standards'

function buildUserStandardFileKey(
  userStandardId: string,
  fileName: string
): string {
  return `${USER_STANDARDS_BUCKET_PREFIX}/${userStandardId}/${normalizeFileName(fileName)}`
}

const PDF_ONLY_ERROR = 'Only PDF files are allowed'

/**
 * User standard as returned by GET /v1/user-standards and GET /v1/user-standards/:id.
 * Same shape as Standard but with scope (not itehScope) and organizationId.
 */
export type UserStandard = {
  id: string
  organizationId: string
  reference: string
  title: string
  type: string
  scope: string | null
  scopeSource: string | null
  withdrawalOjDate: string | null
  inEffect: boolean
  fileName: string | null
  filePathInBucket: string | null
  isImportedFromStandards: boolean
  complianceStudioIds: string[]
  projectRelation: ProjectRelationItem[] | null
  informationForUseRequirements?: InformationForUseStructured | null
  significantHazards?: SignificantHazardsResponse | null
  riskReductionMeasures?: RiskReductionMeasuresResponse | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export const USER_STANDARDS_KEY = 'userStandards'

export function useListUserStandards(options?: { enabled?: boolean }) {
  return useQuery<UserStandard[], Error>({
    queryKey: [USER_STANDARDS_KEY, 'list'],
    queryFn: async () => {
      const response = await apiClient.get<UserStandard[]>('/v1/user-standards')
      return response.data
    },
    enabled: options?.enabled ?? true,
  })
}

export function useUserStandard(
  userStandardId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<UserStandard | null, Error>({
    queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
    queryFn: async () => {
      if (!userStandardId) {
        return null
      }
      const response = await apiClient.get<UserStandard>(
        `/v1/user-standards/${userStandardId}`
      )
      return response.data
    },
    enabled: (options?.enabled ?? true) && !!userStandardId,
  })
}

/** Payload for creating a brand-new user standard. */
export type CreateUserStandardPayload = {
  reference: string
  title: string
  inEffect: boolean
  complianceStudioIds: string[]
  withdrawalOjDate?: string | null
  type?: ManualMachineryStandardType
}

/** Create a brand-new user standard (not from a global standard). */
export function useCreateUserStandard() {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, CreateUserStandardPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<UserStandard>(
        '/v1/user-standards',
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Create a user standard by copying a global standard. */
export function useCreateUserStandardFromStandard() {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, { sourceStandardId: string }>({
    mutationFn: async ({ sourceStandardId }) => {
      const response = await apiClient.post<UserStandard>(
        '/v1/user-standards/from-standard',
        { sourceStandardId }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Soft-delete a user standard. */
export function useDeleteUserStandard() {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, string>({
    mutationFn: async (userStandardId) => {
      const response = await apiClient.delete<UserStandard>(
        `/v1/user-standards/${userStandardId}`
      )
      return response.data
    },
    onSuccess: (_, userStandardId) => {
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
      void queryClient.invalidateQueries({ queryKey: [RISK_REDUCTION_MEASURES_KEY] })
      void queryClient.removeQueries({
        queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
      })
    },
  })
}

/** Get a signed upload URL for a user standard PDF. */
export function useUserStandardUploadUrl(userStandardId: string | null) {
  return useMutation<string, Error, string>({
    mutationFn: async (key: string) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.post<{ url: string }>(
        `/v1/user-standards/${userStandardId}/upload-url`,
        { key }
      )
      return response.data.url
    },
  })
}

/** Update the file metadata after upload (fileName and filePathInBucket). */
export function useUpdateUserStandardFile(userStandardId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<
    UserStandard,
    Error,
    { fileName: string; filePathInBucket: string }
  >({
    mutationFn: async ({ fileName, filePathInBucket }) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.patch<UserStandard>(
        `/v1/user-standards/${userStandardId}/file`,
        { fileName, filePathInBucket }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
      })
    },
  })
}

/** Remove the PDF file from a user standard (sets fileName and filePathInBucket to null). */
export function useRemoveUserStandardFile(userStandardId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, void>({
    mutationFn: async () => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.delete<UserStandard>(
        `/v1/user-standards/${userStandardId}/file`
      )
      return response.data
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Get a signed download URL and open it (e.g. in a new tab). */
export function useUserStandardDownloadUrl(userStandardId: string | null) {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.post<{ url: string }>(
        `/v1/user-standards/${userStandardId}/download-url`
      )
      window.open(response.data.url, '_blank', 'noopener,noreferrer')
    },
  })
}

const EXTRACT_PREVIEW_POLL_MS = 2000
const EXTRACT_PREVIEW_MAX_WAIT_MS = 15 * 60 * 1000

/**
 * Run extract-preview via start + workflow polling: RRMs, significant hazards, information-for-use, and scope
 * (four LLM steps after PDF text extraction). Server allows ~7m for the synchronous extract-preview route; polling
 * waits up to EXTRACT_PREVIEW_MAX_WAIT_MS.
 */
export function useExtractPreview(userStandardId: string | null) {
  const queryClient = useQueryClient()
  const pollReleaseRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      pollReleaseRef.current?.()
    }
  }, [])

  return useMutation<ExtractPreviewResponse, Error, ExtractPreviewRequest | void>({
    mutationFn: async (variables) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const extractSections = variables?.extractSections

      pollReleaseRef.current?.()

      let aborted = false
      const abortController = new AbortController()
      const release = () => {
        aborted = true
        abortController.abort()
      }
      pollReleaseRef.current = release

      try {
        const startResponse = await apiClient.post<{ runId: string }>(
          `/v1/user-standards/${userStandardId}/extract-preview/start`,
          { extractSections }
        )
        const { runId } = startResponse.data

        const startedAt = Date.now()
        let since: number | undefined = undefined

        while (true) {
          if (aborted) {
            throw new DOMException('Aborted', 'AbortError')
          }
          if (Date.now() - startedAt > EXTRACT_PREVIEW_MAX_WAIT_MS) {
            throw new Error(
              'Extraction is taking longer than expected. Refresh the page to see if results are available.'
            )
          }

          const status = await getWorkflowStatus(runId, {
            since,
            signal: abortController.signal,
          })

          applyWorkflowPollBatchEvents(status.events)

          if (status.status === 'completed') {
            const result = status.result
            if (result == null) {
              throw new Error('Extraction completed without a result')
            }
            return result as ExtractPreviewResponse
          }
          if (status.status === 'failed') {
            throw new Error(status.error ?? 'Extraction workflow failed')
          }
          if (status.status === 'cancelled') {
            throw new Error('Extraction was cancelled')
          }

          since = status.latestSequence

          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (aborted) {
                reject(new DOMException('Aborted', 'AbortError'))
              } else {
                resolve()
              }
            }, EXTRACT_PREVIEW_POLL_MS)
          })
        }
      } finally {
        if (pollReleaseRef.current === release) {
          pollReleaseRef.current = null
        }
      }
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** SSE: localize one extracted section for the selected locales (after review, before save). */
export function useSectionLocalizeStream(userStandardId: string | null) {
  return useMutation<SectionLocalizeStreamResult, Error, SectionLocalizeStreamRequest>({
    mutationFn: async (request) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const url = getUserStandardsStreamUrl(
        userStandardId,
        '/section-localize/stream'
      )
      const body = JSON.stringify(buildSectionLocalizeStreamBody(request))
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getUserStandardsStreamHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body,
      })
      if (!response.ok) {
        throw new Error(`Translation failed: ${response.statusText}`)
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let result: SectionLocalizeStreamResult | null = null

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(data) as {
              type: string
              message?: string
              error?: string
              result?: SectionLocalizeStreamResult
            }
            if (
              event.type === WorkflowEventType.Progress &&
              typeof event.message === 'string'
            ) {
              request.onProgress?.(event.message)
            } else if (event.type === WorkflowEventType.WorkflowCompleted) {
              if (event.result != null) {
                result = event.result
              }
            } else if (event.type === WorkflowEventType.WorkflowFailed) {
              throw new Error(event.error ?? 'Translation workflow failed')
            } else if (
              event.type === WorkflowEventType.Error &&
              event.error != null
            ) {
              throw new Error(event.error)
            }
          }
        }
      } finally {
        reader.releaseLock()
      }

      if (result == null) {
        throw new Error('Translation completed without a result')
      }
      return result
    },
  })
}

/** Update significant hazards for a user standard. */
export function useUpdateUserStandardSignificantHazards(
  userStandardId: string | null
) {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, SignificantHazardsPayload>({
    mutationFn: async (payload) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.patch<UserStandard>(
        `/v1/user-standards/${userStandardId}/significant-hazards`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Update information-for-use for a user standard. */
export function useUpdateUserStandardInformationForUse(
  userStandardId: string | null
) {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, InformationForUseUpdatePayload>({
    mutationFn: async (payload) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.patch<UserStandard>(
        `/v1/user-standards/${userStandardId}/information-for-use`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Update scope for a user standard (creates embedding on server when scope is non-empty; null/empty clears). */
export function useUpdateUserStandardScope(userStandardId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, { scope: string | null }>({
    mutationFn: async (payload) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.patch<UserStandard>(
        `/v1/user-standards/${userStandardId}/scope`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Update risk reduction measures for a user standard. */
export function useUpdateUserStandardRiskReductionMeasures(
  userStandardId: string | null
) {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, RiskReductionMeasuresUpdatePayload>({
    mutationFn: async (payload) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.patch<UserStandard>(
        `/v1/user-standards/${userStandardId}/risk-reduction-measures`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/** Payload for PATCH …/extract-preview/apply (atomic apply of preview extraction). */
export type ApplyExtractPreviewPayload = {
  significantHazards: SignificantHazardsPayload
  informationForUse: InformationForUseStructured
  riskReductionMeasures: RiskReductionMeasuresPayload
  /** Omit to leave existing scope unchanged (same behaviour as the old sequential apply). */
  scope?: string | null
}

/** Atomically apply hazards, IFU, RRMs, and optionally scope from extract-preview. */
export function useApplyUserStandardExtractPreview(
  userStandardId: string | null
) {
  const queryClient = useQueryClient()
  return useMutation<UserStandard, Error, ApplyExtractPreviewPayload>({
    mutationFn: async (payload) => {
      if (!userStandardId) {
        throw new Error('Missing user standard id')
      }
      const response = await apiClient.patch<UserStandard>(
        `/v1/user-standards/${userStandardId}/extract-preview/apply`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      if (userStandardId) {
        void queryClient.invalidateQueries({
          queryKey: [USER_STANDARDS_KEY, 'detail', userStandardId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: [USER_STANDARDS_KEY] })
    },
  })
}

/**
 * Upload a PDF for a user standard: get signed URL, PUT to OSS, then PATCH file metadata.
 * Validates PDF (extension and optional type). Throws if not PDF or on upload failure.
 * `onUploadProgress` is called with `null` while the PUT is in flight and `100`
 * after it completes. Fetch does not expose byte-level upload progress.
 */
export async function uploadUserStandardPdf(
  userStandardId: string,
  file: File,
  options: {
    getUploadUrl: (key: string) => Promise<string>
    updateFile: (data: {
      fileName: string
      filePathInBucket: string
    }) => Promise<UserStandard>
    onUploadProgress?: (percent: number | null) => void
  }
): Promise<void> {
  const keyLower = file.name.toLowerCase()
  if (!keyLower.endsWith('.pdf')) {
    throw new Error(PDF_ONLY_ERROR)
  }
  if (file.type && file.type !== 'application/pdf') {
    throw new Error(PDF_ONLY_ERROR)
  }

  assertUploadFileSize(file)
  await assertUploadFileReadable(file)

  const url = await options.getUploadUrl(file.name)
  options.onUploadProgress?.(null)
  await uploadFileToSignedOssUrl(url, file, {
    contentType: 'application/pdf',
    context: {
      feature: 'user-standard',
      fileName: file.name,
      fileSizeBytes: file.size,
      contentType: 'application/pdf',
      userStandardId,
    },
  })
  options.onUploadProgress?.(100)

  const filePathInBucket = buildUserStandardFileKey(userStandardId, file.name)
  await options.updateFile({
    fileName: file.name,
    filePathInBucket,
  })
}
